import React, { useState, useEffect, lazy, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import Login from './views/login/login';
import './App.css';
import { Link } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import Navbar from './components/Navbar/Navbar';
import { useSelector } from 'react-redux';
import { getRoutes } from './helpers/getRoutes';
import { useConfig } from './hooks/useConfig';
import { LoadingProvider } from './context/LoadingContext';
import { Backdrop, CircularProgress } from '@mui/material';
import { useLoading } from './context/LoadingContext';
import { useGetPublicTripDataQuery } from './store/apiSlice';
import { SnackbarProvider } from './context/SnackbarContext';
import { ProductConfigProvider, useProductConfig } from './context/ProductConfigContext';
import { useAuthMiddleware } from './middleware/authMiddleware';

// Lazy loaded components
const Dashboard = React.lazy(() => import('./views/home/home'));
const OtpVerification = React.lazy(() => import('./views/otp-verification/otp-verification'));
const ForgotPassword = React.lazy(() => import('./views/forgot-password/forgot-password'));
const Home = React.lazy(() => import('./views/home/home'));
const Logout = React.lazy(() => import('./views/login/logout'));
const DynamicDashboardPage = React.lazy(() => import('./views/dynamic-dashboard-page/DynamicDashboardPage'));
const PublicTrip = React.lazy(() => import('./views/public-trip/public-trip'));
const Trips = React.lazy(() => import('./views/trips/trips'));
const UpdateStatus = React.lazy(() => import('./views/update-status/UpdateStatus'));
const UnknownRoute = React.lazy(() => import('./views/unknown-route/unknown-route'));

// Updated LayoutWrapper component
const LayoutWrapper = ({ children }) => {
  return (
    <div className="app">
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

// NavbarWrapper component to handle location-based navbar rendering
const NavbarWrapper = ({ isAuthenticated, isInAuthProcess, hideNavbar, navigationItems, handleStorageChange }) => {
  const location = useLocation();
  
  // Don't show navbar on public route
  if (location.pathname === '/public') {
    return null;
  }
  
  // Don't show navbar on updateStatus route
  if (location.pathname.startsWith('/updateStatus')) {
    return null;
  }
  
  // Show navbar if authenticated and not in auth process and not hidden
  if (isAuthenticated && !isInAuthProcess && !hideNavbar) {
    return <Navbar items={navigationItems} handleStorageChange={handleStorageChange} />;
  }
  
  return null;
};

// Add this ProtectedRoute component before the App component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('auth_token'); // Changed from 'token' to 'auth_token'
  const location = useLocation();
  
  // Allow direct access to public route without authentication
  if (location.pathname === '/public') {
    return children;
  }
  
  if (!isAuthenticated) {
    // Store the intended destination before redirecting to login
    const intendedDestination = location.pathname + location.search;
    const searchParams = new URLSearchParams(location.search);

    if (intendedDestination !== '/login' && intendedDestination !== '/otp-verification' && (searchParams.get('token') || searchParams.get('basic_cred'))) {
      localStorage.setItem('intended_destination', intendedDestination);
      // Set auth process flag when redirecting to login
      localStorage.setItem('auth_process_active', 'true');
      // Dispatch custom event to notify components
      window.dispatchEvent(new CustomEvent('authProcessChange', { 
        detail: { isInAuthProcess: true } 
      }));
    }
    return <Navigate to="/login" replace />;
  }

  return children;
};

const DocumentTitle = () => {
  const { config } = useConfig();
  const { featureConfig } = useProductConfig();

  const getLogo = (url) => {
    if (!url) return url;
    return url.replace("https://assetsstatic.s3.ap-south-1.amazonaws.com", "https://staticassests.intusystems.info");
  };

  useEffect(() => {
    const title =
      featureConfig?.white_label?.main_title ||
      config?.feature_config?.white_label?.main_title ||
      config?.main_title ||
      'Intutrack - Single Cell Tracking';
    document.title = title;

    const faviconUrl = getLogo(
      config?.favicon ||
      featureConfig?.white_label?.favicon ||
      './assets/images/intugine-logo.svg'
    );

    const favicon = document.getElementById('favicon');
    if (favicon) {
      favicon.href = faviconUrl ? faviconUrl : './assets/images/intugine-logo.svg';
    }
  }, [config, featureConfig]);

  return null;
};

// Add GlobalLoader component
const GlobalLoader = () => {
  const { loading } = useLoading();
  return (
    <Backdrop
      sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={loading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  );
};

// Component to handle auth middleware
const AuthMiddlewareWrapper = ({ children, onAuthProcessChange }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(localStorage.getItem('auth_token'));
  const location = useLocation();

  const { processQueryAuth, isProcessing } = useAuthMiddleware({
    onAuthSuccess: (result) => {
      // console.log('Auth middleware success:', result);
      // Update authentication state
      setIsAuthenticated(localStorage.getItem('auth_token'));
      // Trigger storage change event to update other components
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'auth_token',
        newValue: localStorage.getItem('auth_token')
      }));
      // Notify parent that auth process is complete
      if (onAuthProcessChange) {
        onAuthProcessChange(false,result?.otherConfig);
      }

    },
    onAuthError: (error) => {
      console.error('Auth middleware error:', error);
      // Notify parent that auth process is complete (even on error)
      if (onAuthProcessChange) {
        onAuthProcessChange(false);
      }
    }
  });

  // Use useEffect to notify parent about auth process state changes
  useEffect(() => {
    if (isProcessing && onAuthProcessChange) {
      onAuthProcessChange(true);
    }
  }, [isProcessing, onAuthProcessChange]);

  // Skip authentication middleware for public route
  if (location.pathname === '/public') {
    return children;
  }

  // Show loading indicator while processing authentication
  if (isProcessing) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
      }}>
        <CircularProgress />
        <div style={{ marginTop: '16px' }}>Processing authentication...</div>
      </div>
    );
  }

  return children;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(localStorage.getItem('auth_token'));
  const [isInAuthProcess, setIsInAuthProcess] = useState(false);
  const { config } = useConfig();
  const [navigationItems, setNavigationItems] = useState(getRoutes(config));
  const intervalRef = useRef(null);
  const eventListenersAddedRef = useRef(false);
  const [hideNavbar, setHideNavbar] = useState(false);


  const handleStorageChange = useCallback(() => {
    const newAuthState = localStorage.getItem('auth_token');
    const wasAuthenticated = isAuthenticated;
    setIsAuthenticated(newAuthState);
    if(config)
      setNavigationItems(getRoutes(config));
    
    // Track authentication state changes
    if (!wasAuthenticated && newAuthState) {
      // User just logged in
      const userInfo = localStorage.getItem('user_info');
      if (userInfo) {
        try {
          const user = JSON.parse(userInfo);
          // Handle user info if needed
        } catch (e) {
          console.warn('Failed to parse user info');
        }
      }
      // Don't set isInAuthProcess here - let the auth process logic handle it
    } else if (wasAuthenticated && !newAuthState) {
      // User just logged out
      // trackLogout();
    }
  }, [isAuthenticated, config]);

  // Check for auth process flag on component mount and when localStorage changes
  useEffect(() => {
    const checkAuthProcess = () => {
      const authProcessActive = localStorage.getItem('auth_process_active');
      const shouldBeInAuthProcess = !!authProcessActive;
      
     
      // Use functional update to avoid closure issues
      setIsInAuthProcess(prevState => {
        if (prevState !== shouldBeInAuthProcess) {
          // setHideNavbar(true)
          return shouldBeInAuthProcess;
        }
        return prevState;
      });
      // console.log("checkAuthProcess isInAuthProcess",isInAuthProcess)
    };

    // Check on mount
    checkAuthProcess();

    // Only add event listeners once
    if (!eventListenersAddedRef.current) {
      // Listen for custom auth process events
      const handleAuthProcessEvent = (e) => {
        if (e.detail && typeof e.detail.isInAuthProcess === 'boolean') {
          setIsInAuthProcess(e.detail.isInAuthProcess);
          // console.log("handleAuthProcessEvent isInAuthProcess",isInAuthProcess)
        } else {
          checkAuthProcess();
        }
      };

      // Listen for storage changes (for cross-tab communication)
      const handleStorageEvent = (e) => {
        if (e.key === 'auth_process_active') {
          checkAuthProcess();
        }
      };

      window.addEventListener('authProcessChange', handleAuthProcessEvent);
      window.addEventListener('storage', handleStorageEvent);
      eventListenersAddedRef.current = true;

      // Check periodically for changes (reduced frequency to avoid conflicts)
      // intervalRef.current = setInterval(checkAuthProcess, 5000);

  

      return () => {
        // console.log("removing event listeners")
        window.removeEventListener('authProcessChange', handleAuthProcessEvent);
        window.removeEventListener('storage', handleStorageEvent);
        // if (intervalRef.current) {
        //   clearInterval(intervalRef.current);
        //   intervalRef.current = null;
        // }
        eventListenersAddedRef.current = false;
      };
    }
  }, []); // Empty dependency array to run only once

  // Cleanup effect to ensure auth process flag is cleared on unmount or navigation issues
  useEffect(() => {
    const cleanupAuthProcess = () => {
      // Clear auth process flag if it's been set for too long (5 minutes)
      const authProcessActive = localStorage.getItem('auth_process_active');
      if (authProcessActive) {
        const authProcessTime = localStorage.getItem('auth_process_time');
        const currentTime = Date.now();
        const timeDiff = currentTime - (authProcessTime ? parseInt(authProcessTime) : 0);
        
        // If auth process has been active for more than 5 minutes, clear it
        if (timeDiff > 5 * 60 * 1000) {
          localStorage.removeItem('auth_process_active');
          localStorage.removeItem('auth_process_time');
          setIsInAuthProcess(false);
          window.dispatchEvent(new CustomEvent('authProcessChange', { 
            detail: { isInAuthProcess: false } 
          }));
        }
      }
    };

    // Check for stale auth process on mount
    cleanupAuthProcess();

    // Set up periodic cleanup (only if not already set up)
    const cleanupInterval = setInterval(cleanupAuthProcess, 30000); // Check every 30 seconds

    return () => {
      clearInterval(cleanupInterval);
    };
  }, []); // Empty dependency array to run only once

  useEffect(() => {
    // console.log("navigationItems updated",navigationItems)
  }, [navigationItems])

  useEffect(() => {
    // console.log("config",config)
    if(config){
      setNavigationItems(getRoutes(config));
    }

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [ config]);

  // Memoize the onAuthProcessChange callback to prevent unnecessary re-renders
  const handleAuthProcessChange = useCallback((isInProcess,otherConfig={}) => {
   if(otherConfig?.hideNavbar)
    setHideNavbar(true)

    setIsInAuthProcess(isInProcess);
  }, []);

  return (
    <Provider store={store}>
      <ProductConfigProvider>
        <DocumentTitle />
        <SnackbarProvider>
          <LoadingProvider>
            <Router>
              <GlobalLoader />
              <AuthMiddlewareWrapper onAuthProcessChange={handleAuthProcessChange}>
                <React.Suspense fallback={<div>Loading...</div>}>
                  <NavbarWrapper 
                    isAuthenticated={isAuthenticated}
                    isInAuthProcess={isInAuthProcess}
                    hideNavbar={hideNavbar}
                    navigationItems={navigationItems}
                    handleStorageChange={handleStorageChange}
                  />
                  <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<Login handleStorageChange={handleStorageChange} />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/public" element={<PublicTrip />} />
                    {/* Token route for status update - handled by auth middleware */}
                    <Route path="/updateStatus/:updateStatus/:tripId/:token" element={
                      <LayoutWrapper>
                        <React.Suspense fallback={<div>Loading...</div>}>
                          <UpdateStatus />
                        </React.Suspense>
                      </LayoutWrapper>
                    } />
                    
                    {/* Logout route */}
                    <Route path="/logout" element={<Logout handleStorageChange={handleStorageChange} />}  />
                    
                    {/* Protected routes - with Layout wrapper */}
                    <Route path="/home" element={
                      <ProtectedRoute>
                        <LayoutWrapper>
                          <Home />
                        </LayoutWrapper>
                      </ProtectedRoute>
                    } />

                    <Route path="/trips" element={
                      <ProtectedRoute>
                        <LayoutWrapper>
                          <React.Suspense fallback={<div>Loading...</div>}>
                            <Trips />
                          </React.Suspense>
                        </LayoutWrapper>
                      </ProtectedRoute>
                    } />

                    {/* Add routes for all navigation items */}
                    {navigationItems.map(item => {
                      const blockedPaths = [
                        "/vehicle",
                        "/user-management",
                        "/lcr",
                        "/device-mapping",
                        "/analytics-new",
                        "/vehicle-new",
                        "/device_mgmt",
                        "/consents",
                        "/recharge",
                        "/advancedanalytics",
                        "/notifications",
                        "/support",
                        "/master-data",
                        "/billing",
                        "/freight-module",
                        "/revenue",
                        "/document"

                      ];

                      const isBlocked = blockedPaths.includes(item.path);

                      const element = isBlocked
                        ? <UnknownRoute key={item.path} />
                        : React.createElement(
                            React.lazy(() => import(`./views/${item.id}/${item.id}`))
                          );

                      return (
                        <Route
                          key={item.id}
                          path={item.path}
                          element={
                            <ProtectedRoute>
                              <LayoutWrapper>
                                <React.Suspense fallback={<div>Loading...</div>}>
                                  {element}
                                </React.Suspense>
                              </LayoutWrapper>
                            </ProtectedRoute>
                          }
                        />
                      );
                    })}

                    <Route path="/otp-verification" element={
                      <ProtectedRoute>
                        <LayoutWrapper>
                          <OtpVerification handleStorageChange={handleStorageChange} />
                        </LayoutWrapper>
                      </ProtectedRoute>
                    } />
                    
                    {/* Redirect root to dashboard */}
                    <Route path="/" element={<Navigate to="/home" replace />} />

                    {/* New dynamic route for iframe dashboard */}
                    <Route path="/page" element={
                      <ProtectedRoute>
                        <LayoutWrapper>
                          <React.Suspense fallback={<div>Loading...</div>}>
                            <DynamicDashboardPage />
                          </React.Suspense>
                        </LayoutWrapper>
                      </ProtectedRoute>
                    } />

                    {/* Handle 404 - page not found */}
                    <Route
                      path="*"
                      element={
                        <ProtectedRoute>
                          <LayoutWrapper>
                            <React.Suspense fallback={<div>Loading...</div>}>
                              < UnknownRoute unknown={true} />
                            </React.Suspense>
                          </LayoutWrapper>
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </React.Suspense>
              </AuthMiddlewareWrapper>
            </Router>
          </LoadingProvider>
        </SnackbarProvider>
      </ProductConfigProvider>
    </Provider>
  );
}

export default App;
